import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { AgGridModule } from 'ag-grid-angular';
import { Observable, of } from 'rxjs';
import { map, startWith, catchError } from 'rxjs/operators';

import { AssessmentsService } from '../../service/entity-services/assessments.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';

import { EntityList } from '../../objects/entity-list';
import { Assessment } from '../../objects/assessment';

import * as assessmentColumns from '../../shared/entity-columns/assessment-columns';

@Component({
  selector: 'app-assessments',
  templateUrl: './assessments.component.html',
  styleUrls: ['./assessments.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatDividerModule,
    MatSidenavModule,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule
  ]
})
export class AssessmentsComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsStore = inject(PermissionsStoreService);
  private cdr = inject(ChangeDetectorRef);
  public assessmentsService = inject(AssessmentsService);

  @ViewChild('assessmentsSidenavActions') assessmentsSidenavActions!: TemplateRef<any>;

  // Flatten assessmentData wrapper for AG Grid columns
  // v2 orgs read the light /summary rows (people come back as display names); the rest keep
  // flattening the full legacy records.
  allAssessments$: Observable<any[]> = this.assessmentsService.usesV2
    ? this.assessmentsService.getSummaries().pipe(
        catchError(err => {
          console.error('[Assessments] could not load the list:', err);
          return of([]);
        })
      )
    : this.assessmentsService.allAssessments$.pipe(
        map(assessments => assessments.map((a: any) => {
          if (a.assessmentData) {
            return { ...a.assessmentData, assessmentNum: a.assessmentNum || a.assessmentData.assessmentNum };
          }
          return a;
        }))
      );

  // UI state
  columnChooserControl = new FormControl();
  customizeColumnsUi = false;
  waitingForServer = false;
  showNameSavedView = false;
  isInitializing = true;

  isViewer = this.permissionsStore.isViewer;

  // Column configuration
  defaultCols: any[] = [
    assessmentColumns.colName,
    assessmentColumns.colType,
    assessmentColumns.colStatus,
    assessmentColumns.colOwner,
    assessmentColumns.colAssessor,
    assessmentColumns.colFinalReviewer,
    assessmentColumns.colSourceRecord,
    assessmentColumns.colCriticalDate,
    assessmentColumns.colTotalScore
  ];

  selectedCols = this.defaultCols;

  availableColumns: any[] = [];
  availableColumnSelectList: { value: any; viewValue: string }[] = [];
  columnSelectorControl = new FormControl<string>('');
  selectedColumnsList: any[] = [];
  filteredColumns$!: Observable<{ value: any; viewValue: string }[]>;

  public defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab']
  };
  public showFloatingFilters = false;

  public tooltipShowDelay = 300;
  public tooltipHideDelay = 10000;
  public tooltipInteraction = true;

  public frameworkComponents = {};

  constructor() {
    super();
    this.entityType = 'assessments';
    this.buildAvailableColumns();
    this.initializeSessionStorage();
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Assessments');

    this.filteredColumns$ = this.columnSelectorControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterColumns(value || ''))
    );
  }

  override onGridReady(params: any): void {
    super.onGridReady(params);

    this.completeInitialization().catch(err => {
      console.error('[onGridReady] Error during initialization:', err);
      this.isInitializing = false;
    });
  }

  private async completeInitialization(): Promise<void> {
    this.suspendSessionTracking = true;

    const hasSessionState = this.sessionStorageKey && sessionStorage.getItem(this.sessionStorageKey);

    if (hasSessionState) {
      this.loadSessionState();
      this.suspendSessionTracking = false;
    } else {
      await this.loadUserViews();

      const hasNewFormatView = this.savedNamedViews && (this.savedNamedViews as any).gridState;
      const hasOldFormatView =
        this.savedNamedViews && ((this.savedNamedViews as any).columnState || (this.savedNamedViews as any).savedViewData);

      if (hasNewFormatView || hasOldFormatView) {
        await this.loadNamedView(this.savedNamedViews);
        this.suspendSessionTracking = false;
      } else {
        this.autoSizeAll();
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }

    this.isInitializing = false;
    this.cdr.detectChanges();
  }

  private buildAvailableColumns(): void {
    this.availableColumns = [...assessmentColumns.allColumns];
    this.availableColumns = this.availableColumns.filter(col => col && col.colId && col.headerName);

    const uniqueColsMap = new Map();
    this.availableColumns.forEach(col => {
      if (!uniqueColsMap.has(col.colId)) {
        uniqueColsMap.set(col.colId, col);
      }
    });
    this.availableColumns = Array.from(uniqueColsMap.values());

    this.availableColumnSelectList = this.availableColumns.map(col => ({
      value: col,
      viewValue: col.headerName
    }));
    this.availableColumnSelectList.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  private _filterColumns(value: string | any): { value: any; viewValue: string }[] {
    if (!value || typeof value !== 'string') {
      return this.availableColumnSelectList;
    }
    const filterValue = value.toLowerCase();
    return this.availableColumnSelectList.filter(option => option.viewValue.toLowerCase().includes(filterValue));
  }

  displayColumnFn(): string {
    return '';
  }

  ngAfterViewInit(): void {
    this.sidenavContentService.updateSidenavContent(this.assessmentsSidenavActions);
  }

  ngOnDestroy(): void {
    this.sidenavContentService.updateSidenavContent(null);
  }

  createNewAssessment(): void {
    this.router.navigateByUrl('/securehome/new-assessment');
  }

  onCellClicked(event: any): void {
    if (event.data?.assessmentNum) {
      this.router.navigate(['/securehome/assessment', event.data.assessmentNum]);
    }
  }

  onSearchChange(searchText: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchText);
    }
  }

  selectColumn(column: any): void {
    if (column && !this.selectedColumnsList.find(c => c.colId === column.colId)) {
      this.selectedColumnsList.push(column);
      setTimeout(() => {
        this.columnSelectorControl.setValue('', { emitEvent: false });
      }, 0);
    }
  }

  removeColumn(column: any): void {
    const index = this.selectedColumnsList.findIndex(c => c.colId === column.colId);
    if (index >= 0) {
      this.selectedColumnsList.splice(index, 1);
    }
  }

  applyColumns(): void {
    if (this.gridApi && this.selectedColumnsList.length > 0) {
      this.selectedCols = this.selectedColumnsList;
      this.gridApi.setGridOption('columnDefs', this.selectedCols);
      this.customizeColumnsUi = false;
      this.markSessionAsModified();
    }
  }

  openCustomizeColumnsDialog(): void {
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      const visibleColIds = currentState.filter((col: any) => !col.hide).map((col: any) => col.colId);
      this.selectedColumnsList = this.availableColumns.filter(col => visibleColIds.includes(col.colId));
    } else {
      this.selectedColumnsList = [...this.selectedCols];
    }
    this.customizeColumnsUi = true;
  }

  toggleFloatingFilters(): void {
    this.showFloatingFilters = !this.showFloatingFilters;

    if (this.gridApi) {
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });

      allColumnIds.forEach(colId => {
        this.gridApi.setColumnFilterProperty(colId, 'floatingFilter', this.showFloatingFilters);
      });

      this.gridApi.refreshHeader();
    }
  }

  exportCSV(): void {
    if (this.gridApi) {
      const params = {
        processCellCallback: this.formattingFunction,
        fileName: 'assessments_export.csv'
      };
      this.gridApi.exportDataAsCsv(params);
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      const excelParams = {
        processCellCallback: this.formattingFunction,
        fileName: 'assessments_export.xlsx'
      };
      this.gridApi.exportDataAsExcel(excelParams);
    }
  }
}
